// Studio arrangement preview player — real in-browser audio synthesis (preview groove),
// mobile-safe (single shared, resumed AudioContext like Clip Lab). This is a PREVIEW
// instrument so producers can hear/feel the arrangement in the browser on any device;
// the final audio still comes from their DAW via the exported package.

const AC = typeof window !== "undefined" ? (window.AudioContext || window.webkitAudioContext) : null;
let ctx = null, master = null;
let running = false, reTimer = null, live = [], raf = null, startT = 0, cycleSec = 8, posCb = null;
let isMutedFn = () => false;
let cfg = { bpm: 100, root: 0 };

function getCtx() {
  if (!AC) return null;
  if (!ctx || ctx.state === "closed") {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.85;
    master.connect(ctx.destination);
  }
  return ctx;
}

export function setMasterVolume(v) {
  if (ctx && master) master.gain.setTargetAtTime(Math.max(0, Math.min(1, v)), ctx.currentTime, 0.02);
}

const KEYMAP = { C: 0, "C#": 1, DB: 1, D: 2, "D#": 3, EB: 3, E: 4, F: 5, "F#": 6, GB: 6, G: 7, "G#": 8, AB: 8, A: 9, "A#": 10, BB: 10, B: 11 };
export function keyRoot(k) {
  if (!k) return 0;
  const m = String(k).trim().toUpperCase().replace("♯", "#").replace("♭", "B");
  const letter = m.match(/^[A-G][#B]?/);
  return letter ? (KEYMAP[letter[0]] ?? 0) : 0;
}

const midi = (m) => 440 * Math.pow(2, (m - 69) / 12);

function voice(freq, type, t, dur, vol) {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = type; o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(g).connect(master); o.start(t); o.stop(t + dur + 0.05); live.push(o);
}

function noise(t, dur, vol, hp) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const s = ctx.createBufferSource(); s.buffer = buf;
  const g = ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  const f = ctx.createBiquadFilter(); f.type = "highpass"; f.frequency.value = hp || 6000;
  s.connect(f).connect(g).connect(master); s.start(t); s.stop(t + dur + 0.02); live.push(s);
}

// Schedule one 2-bar (32 x 16th) preview block from `when`. Mute/solo checked per block.
function scheduleBlock(when) {
  const spb = 60 / cfg.bpm / 4; // seconds per 16th
  const steps = 32;
  const root = cfg.root;
  const on = (name) => !isMutedFn(name);

  if (on("Drums")) {
    for (let i = 0; i < steps; i++) {
      const t = when + i * spb; const beat = i % 16;
      if (beat === 0 || beat === 8 || beat === 14) voice(90, "sine", t, 0.18, 0.34);         // kick
      if (beat === 4 || beat === 12) noise(t, 0.13, 0.26, 1400);                              // snare
      if (i % 2 === 0) noise(t, 0.03, 0.07, 9000);                                            // hat
    }
  }
  if (on("Percussion")) {
    for (let i = 0; i < steps; i++) if (i % 4 === 2) noise(when + i * spb, 0.04, 0.06, 7000);
  }
  const sub = 36 + root;
  [0, 16].forEach((b) => {
    const t = when + b * spb;
    if (on("808")) voice(midi(sub), "sine", t, spb * 8, 0.3);
    if (on("Bass")) voice(midi(sub + 12), "triangle", t + spb * 4, spb * 3, 0.16);
  });
  const kr = 60 + root;
  [0, 16].forEach((b) => {
    const t = when + b * spb;
    if (on("Keys")) [0, 4, 7].forEach((iv) => voice(midi(kr + iv), "triangle", t, spb * 14, 0.07));
  });
  if (on("FX / Atmos")) voice(midi(72 + root), "sine", when, spb * 30, 0.03);

  return steps * spb;
}

export async function startArrangement({ bpm = 100, root = 0, isMuted = () => false, onPos } = {}) {
  const c = getCtx();
  if (!c) return { ok: false, reason: "unsupported" };
  try { if (c.state !== "running") await c.resume(); } catch (e) { /* noop */ }
  cfg.bpm = bpm; cfg.root = root; isMutedFn = isMuted; posCb = onPos || null;
  stopArrangement(false);
  running = true;
  const blockSec = scheduleBlock(c.currentTime + 0.06);
  reTimer = setInterval(() => { if (!running) return; live = []; scheduleBlock(ctx.currentTime + 0.02); }, Math.max(400, blockSec * 1000));
  cycleSec = blockSec * 2; startT = c.currentTime;
  const clock0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
  const tick = () => {
    if (!running) return;
    const el = ((typeof performance !== "undefined" ? performance.now() : Date.now()) - clock0) / 1000;
    posCb && posCb((el % cycleSec) / cycleSec);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return { ok: c.state === "running", reason: c.state !== "running" ? "muted" : null };
}

export function stopArrangement(clearPos = true) {
  running = false;
  if (reTimer) { clearInterval(reTimer); reTimer = null; }
  if (raf) { cancelAnimationFrame(raf); raf = null; }
  live.forEach((o) => { try { o.stop(); } catch (e) { /* noop */ } });
  live = [];
  if (clearPos && posCb) posCb(null);
}

export function updateMuteFn(fn) { isMutedFn = fn; }
export function isPlaying() { return running; }
